// SCR-CONSOLE-002 — جزئیات مستأجر (US-PLT-080، 005، 033): تب‌های خلاصه، Entitlement و Audit؛ اقدام‌ها: تعلیق (دلیل + حالت هدف)،
// فعال‌سازی مجدد، تغییر شناسه عمومی با دلیل، تلاش مجدد Provisioning. بدون هیچ داده مراجعه‌کننده (AC-PLT-080.1).
// Polling تا Active یا ProvisioningFailed پس از ایجاد (SCR-CONSOLE-003).
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { unwrap, type ConsoleTenantDetail } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DetailPage,
  faDigits,
  FormDialog,
  JalaliDate,
  KeyValueList,
  ReasonDialog,
  StatusChip,
  TextFieldRHF,
  type DetailTab,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';
import { usePlatformAccess } from '../../access';
import {
  accessModeTone,
  consoleKeys,
  isProvisioning,
  PROVISIONING_POLL_MS,
  tenantStatusTone,
  toNumber,
} from '../../api';
import { limitLabelKey } from '../../catalog';
import { zSlug } from '../../slug';
import { AuditTable } from '../audit/AuditTable';
import { EntitlementsTab } from './EntitlementsTab';
import { OperationsTab } from './OperationsTab';
import { SubscriptionTab } from './SubscriptionTab';

export const SCR_CONSOLE_002 = 'SCR-CONSOLE-002';

type SuspendTarget = 'ReadOnly' | 'Locked';

function SummaryTab({ tenant }: { tenant: ConsoleTenantDetail }) {
  const { t } = useTranslation('console');
  const subscription = tenant.subscription;
  return (
    <Stack spacing={2} data-testid="summary-tab">
      <Paper variant="outlined" sx={{ p: 2 }}>
        <KeyValueList
          columns={3}
          items={[
            {
              labelKey: 'console:tenant.status',
              value: (
                <StatusChip
                  tone={tenantStatusTone(tenant.status)}
                  labelKey={`console:tenant_status.${tenant.status}`}
                />
              ),
              testId: 'tenant-status',
            },
            {
              labelKey: 'console:tenant.access_mode',
              value: (
                <StatusChip
                  tone={accessModeTone(tenant.accessMode)}
                  labelKey={`console:access_mode.${tenant.accessMode}`}
                />
              ),
              testId: 'tenant-access-mode',
            },
            {
              labelKey: 'console:tenant.access_mode_reason',
              value: tenant.accessModeReason,
              testId: 'tenant-access-reason',
            },
            {
              labelKey: 'console:tenant.slug',
              value: <bdi dir="ltr">{tenant.slug}</bdi>,
              testId: 'tenant-slug',
            },
            { labelKey: 'console:tenant.legal_name', value: tenant.legalName },
            { labelKey: 'console:tenant.center_type', value: t(`console:center_type.${tenant.centerType}`) },
            {
              labelKey: 'console:tenant.plan',
              value: subscription
                ? t('console:tenant.plan_value', {
                    code: subscription.planCode,
                    version: faDigits(toNumber(subscription.planVersion) ?? 0),
                  })
                : null,
              testId: 'tenant-plan',
            },
            {
              labelKey: 'console:tenant.period_start',
              value: subscription && <JalaliDate value={subscription.periodStart} />,
            },
            {
              labelKey: 'console:tenant.addons',
              value: tenant.addons.length ? <bdi dir="ltr">{tenant.addons.join(', ')}</bdi> : null,
            },
            {
              labelKey: 'console:tenant.active_branches',
              value: faDigits(toNumber(tenant.activeBranches) ?? 0),
            },
            {
              labelKey: 'console:tenant.default_ownership_type',
              value: t(`console:ownership_type.${tenant.defaultOwnershipType}`),
            },
            {
              labelKey: 'console:tenant.owner_mobile',
              value: <bdi dir="ltr">{faDigits(tenant.ownerMobileMasked)}</bdi>,
            },
            { labelKey: 'console:tenant.is_demo', value: t(tenant.isDemo ? 'console:yes' : 'console:no') },
            {
              labelKey: 'console:tenant.created_at',
              value: <JalaliDate value={tenant.createdAt} withTime />,
            },
            {
              labelKey: 'console:tenant.activated_at',
              value: tenant.activatedAt && <JalaliDate value={tenant.activatedAt} withTime />,
            },
            {
              labelKey: 'console:tenant.suspended_at',
              value: tenant.suspendedAt && <JalaliDate value={tenant.suspendedAt} withTime />,
            },
            // AP-PLT-05 (AC-PLT-080.1): تاریخ Go-Live و موجودی اعتبار پیامک.
            {
              labelKey: 'console:tenant.went_live_at',
              value: tenant.wentLiveAt && <JalaliDate value={tenant.wentLiveAt} withTime />,
              testId: 'tenant-went-live',
            },
            {
              labelKey: 'console:tenant.sms_credit',
              value:
                tenant.smsCreditBalance === null || tenant.smsCreditBalance === undefined ? null : (
                  <bdi dir="ltr">{faDigits(toNumber(tenant.smsCreditBalance) ?? 0)}</bdi>
                ),
            },
            { labelKey: 'console:tenant.last_activity', value: null },
          ]}
        />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h3" component="h2" gutterBottom>
          {t('console:tenant.usage_title')}
        </Typography>
        <Table size="small" aria-label={t('console:tenant.usage_title')} data-testid="limit-usage">
          <TableHead>
            <TableRow>
              <TableCell>{t('console:tenant.usage.limit')}</TableCell>
              <TableCell>{t('console:tenant.usage.used')}</TableCell>
              <TableCell>{t('console:tenant.usage.max')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenant.limits.map((limit) => {
              const used = toNumber(limit.used) ?? 0;
              const max = toNumber(limit.max);
              return (
                <TableRow key={limit.key} data-limit={limit.key}>
                  <TableCell>{t(limitLabelKey(limit.key))}</TableCell>
                  <TableCell>{faDigits(used)}</TableCell>
                  <TableCell>{max === null ? t('console:tenant.usage.unlimited') : faDigits(max)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="caption" color="text.secondary">
        {t('console:tenant.no_patient_data')}
      </Typography>
    </Stack>
  );
}

const slugSchema = z.object({
  slug: zSlug(),
  reason: z.string().trim().min(3, { error: 'console:validation.reason_min' }).max(500, {
    error: 'common:validation.too_long',
  }),
});
type SlugForm = z.input<typeof slugSchema>;

function ChangeSlugDialog({
  tenant,
  open,
  onClose,
}: {
  tenant: ConsoleTenantDetail;
  open: boolean;
  onClose: () => void;
}) {
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError, reset } = useForm<SlugForm>({
    resolver: zodResolver(slugSchema),
    values: { slug: tenant.slug, reason: '' },
  });
  const change = useMutation({
    mutationFn: (body: SlugForm) =>
      unwrap(
        api.PUT('/api/v1/platform/console/tenants/{id}/slug', {
          params: { path: { id: tenant.id } },
          body: { slug: body.slug.trim(), reason: body.reason.trim() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (updated) => {
      queryClient.setQueryData(consoleKeys.tenant(tenant.id), updated);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.tenantsRoot });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['slug', 'reason'],
        codeFields: { 'PHX-PLT-0101': 'slug' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="console:tenant.actions.change_slug_title"
      onSubmit={() => void handleSubmit((values) => change.mutate(values))()}
      onClose={() => {
        reset();
        onClose();
      }}
      isDirty={formState.isDirty}
      isSubmitting={change.isPending}
      submitError={submitError}
    >
      <TextFieldRHF
        control={control}
        name="slug"
        labelKey="console:tenants.create.slug"
        helperKey="console:tenants.create.slug_help"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', autoComplete: 'off' } }}
      />
      <TextFieldRHF
        control={control}
        name="reason"
        labelKey="common:dialogs.reason_label"
        required
        multiline
        minRows={2}
        fullWidth
      />
    </FormDialog>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('summary');
  const [dialog, setDialog] = useState<'suspend' | 'reactivate' | 'slug' | null>(null);
  const [target, setTarget] = useState<SuspendTarget>('ReadOnly');

  const tenant = useQuery({
    queryKey: consoleKeys.tenant(id),
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/tenants/{id}', { params: { path: { id } } })),
    refetchInterval: (query) => (isProvisioning(query.state.data) ? PROVISIONING_POLL_MS : false),
  });

  const onUpdated = (updated: ConsoleTenantDetail) => {
    queryClient.setQueryData(consoleKeys.tenant(id), updated);
    void queryClient.invalidateQueries({ queryKey: consoleKeys.tenantsRoot });
    void queryClient.invalidateQueries({ queryKey: ['platform', 'console', 'audit'] });
    setDialog(null);
  };

  const suspend = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}:suspend', {
          params: { path: { id } },
          body: { reason, targetMode: target },
        }),
      ),
    onSuccess: onUpdated,
  });
  const reactivate = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}:reactivate', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: onUpdated,
  });
  const retry = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}:retry-provisioning', { params: { path: { id } } }),
      ),
    onSuccess: onUpdated,
  });

  const data = tenant.data;
  const tabs: DetailTab[] = data
    ? [
        { id: 'summary', labelKey: 'console:tenant.tabs.summary', content: <SummaryTab tenant={data} /> },
        {
          id: 'entitlements',
          labelKey: 'console:tenant.tabs.entitlements',
          content: <EntitlementsTab tenant={data} />,
        },
        // AP-PLT-05: اعتبار پیامک و دسترسی پشتیبانی.
        {
          id: 'operations',
          labelKey: 'console:tenant.tabs.operations',
          content: <OperationsTab tenant={data} />,
        },
        // AP-PLT-07: اشتراک، تمدید Trial، خاتمه، محیط نمایشی، انتقال مالکیت و گواهی حذف.
        {
          id: 'subscription',
          labelKey: 'console:tenant.tabs.subscription',
          content: <SubscriptionTab tenant={data} />,
        },
        ...(access.can('platform.audit.view')
          ? [
              {
                id: 'audit',
                labelKey: 'console:tenant.tabs.audit',
                content: <AuditTable filters={{ tenantId: data.id }} showTenant={false} />,
              },
            ]
          : []),
      ]
    : [];

  const canSuspend = access.can('platform.tenant.suspend');
  const canManage = access.can('platform.tenant.create');

  const actions = data ? (
    <>
      {canSuspend && data.status === 'Active' && (
        <Button
          color="error"
          variant="outlined"
          onClick={() => setDialog('suspend')}
          data-testid="tenant-suspend"
        >
          {t('console:tenant.actions.suspend')}
        </Button>
      )}
      {canSuspend && data.status === 'Suspended' && (
        <Button variant="contained" onClick={() => setDialog('reactivate')} data-testid="tenant-reactivate">
          {t('console:tenant.actions.reactivate')}
        </Button>
      )}
      {canManage && (data.status === 'Active' || data.status === 'Suspended') && (
        <Button variant="outlined" onClick={() => setDialog('slug')} data-testid="tenant-change-slug">
          {t('console:tenant.actions.change_slug')}
        </Button>
      )}
      {canManage && data.status === 'ProvisioningFailed' && (
        <Button
          variant="contained"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
          data-testid="tenant-retry-provisioning"
        >
          {t('console:tenant.actions.retry_provisioning')}
        </Button>
      )}
    </>
  ) : undefined;

  return (
    <>
      {data && isProvisioning(data) && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="provisioning-progress" role="status">
          <AlertTitle>{t('console:tenant.provisioning.title')}</AlertTitle>
          {t('console:tenant.provisioning.description')}
          <LinearProgress sx={{ mt: 1 }} aria-label={t('console:tenant.provisioning.title')} />
        </Alert>
      )}
      {data?.status === 'ProvisioningFailed' && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="provisioning-failed">
          <AlertTitle>{t('console:tenant.provisioning.failed_title')}</AlertTitle>
          {t('console:tenant.provisioning.failed_description')}
          {data.provisioningError && (
            <Typography variant="caption" component="div">
              <bdi dir="ltr">{data.provisioningError}</bdi>
            </Typography>
          )}
        </Alert>
      )}
      {retry.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('console:tenant.actions.retry_failed')}
        </Alert>
      ) : null}
      <DetailPage
        screenId={SCR_CONSOLE_002}
        isLoading={tenant.isPending}
        error={tenant.error}
        onRetry={() => void tenant.refetch()}
        titleKey="console:tenant.title"
        titleValues={{ name: data?.displayName ?? '' }}
        badges={
          data && (
            <StatusChip
              tone={tenantStatusTone(data.status)}
              labelKey={`console:tenant_status.${data.status}`}
            />
          )
        }
        actions={actions}
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      />
      <ReasonDialog
        open={dialog === 'suspend'}
        titleKey="console:tenant.actions.suspend_title"
        messageKey="console:tenant.actions.suspend_message"
        confirmKey="console:tenant.actions.suspend"
        busy={suspend.isPending}
        error={suspend.error}
        onCancel={() => {
          suspend.reset();
          setDialog(null);
        }}
        onConfirm={(reason) => suspend.mutate(reason)}
      >
        <RadioGroup
          value={target}
          onChange={(event) => setTarget(event.target.value as SuspendTarget)}
          aria-label={t('console:tenant.actions.target_mode')}
          sx={{ mb: 2 }}
          data-testid="suspend-target"
        >
          <FormControlLabel
            value="ReadOnly"
            control={<Radio />}
            label={t('console:tenant.actions.target_read_only')}
          />
          <FormControlLabel
            value="Locked"
            control={<Radio />}
            label={t('console:tenant.actions.target_locked')}
          />
        </RadioGroup>
      </ReasonDialog>
      <ReasonDialog
        open={dialog === 'reactivate'}
        titleKey="console:tenant.actions.reactivate_title"
        messageKey="console:tenant.actions.reactivate_message"
        confirmKey="console:tenant.actions.reactivate"
        destructive={false}
        busy={reactivate.isPending}
        error={reactivate.error}
        onCancel={() => {
          reactivate.reset();
          setDialog(null);
        }}
        onConfirm={(reason) => reactivate.mutate(reason)}
      />
      {data && <ChangeSlugDialog tenant={data} open={dialog === 'slug'} onClose={() => setDialog(null)} />}
    </>
  );
}
